import React, { useState, useEffect, useRef } from 'react';
import { MapPin, Loader2, X, Check } from 'lucide-react';
import { usePlaces } from '../../context/places/PlacesContext';

const generateSessionToken = () => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

const LocationAutocompleteInput = ({
  value,
  onChange,
  placeholder = 'Search location (e.g. Geeta Fast Food)...',
  required = false,
  className = '',
  id
}) => {
  const { searchPlaces, fetchPlaceDetails } = usePlaces();

  const [query, setQuery] = useState(() => {
    if (!value) return '';
    if (typeof value === 'string') return value;
    return value.name || value.pickup_location || value.drop_location || '';
  });
  const [address, setAddress] = useState(() => {
    if (!value || typeof value === 'string') return '';
    return value.address || '';
  });
  const [suggestions, setSuggestions] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [isResolvingDetails, setIsResolvingDetails] = useState(false);
  const [hasSelectedPlace, setHasSelectedPlace] = useState(() => {
    return Boolean(value && typeof value === 'object' && value.placeId);
  });

  const sessionTokenRef = useRef(null);
  const wrapperRef = useRef(null);
  const activeQueryRef = useRef('');
  const debounceTimerRef = useRef(null);

  // Synchronize when value prop changes externally
  useEffect(() => {
    let newName = '';
    let newAddress = '';
    if (value) {
      if (typeof value === 'string') {
        newName = value;
      } else {
        newName = value.name || value.pickup_location || value.drop_location || '';
        newAddress = value.address || '';
      }
    }
    setQuery(newName);
    setAddress(newAddress);
    setHasSelectedPlace(Boolean(value && typeof value === 'object' && value.placeId));
  }, [value]);

  // Handle outside click to close dropdown
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Handle input typing with debounce and session token
  const handleInputChange = (e) => {
    const val = e.target.value;
    setQuery(val);
    setHasSelectedPlace(false);
    setAddress('');

    // Inform parent of current manual text
    if (onChange) {
      onChange({
        name: val,
        address: '',
        latitude: null,
        longitude: null,
        placeId: null,
        is_custom: true
      });
    }

    if (!sessionTokenRef.current) {
      sessionTokenRef.current = generateSessionToken();
    }

    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    const trimmed = val.trim();
    if (trimmed.length < 3) {
      setSuggestions([]);
      setIsOpen(false);
      setIsSearching(false);
      return;
    }

    setIsSearching(true);
    setIsOpen(true);
    activeQueryRef.current = trimmed;

    debounceTimerRef.current = setTimeout(async () => {
      try {
        const results = await searchPlaces(trimmed, sessionTokenRef.current);
        // Only apply if query has not changed in the meantime
        if (activeQueryRef.current === trimmed) {
          setSuggestions(results || []);
          setIsSearching(false);
        }
      } catch (err) {
        console.error('Autocomplete search error:', err);
        if (activeQueryRef.current === trimmed) {
          setSuggestions([]);
          setIsSearching(false);
        }
      }
    }, 300);
  };

  // Handle selecting a place suggestion
  const handleSelectSuggestion = async (item) => {
    setIsResolvingDetails(true);
    setIsOpen(false);
    setQuery(item.name);

    try {
      const details = await fetchPlaceDetails(item.placeId, sessionTokenRef.current);
      // Reset session token upon selection as per Google Places API session guidelines
      sessionTokenRef.current = null;

      const structured = {
        name: details?.name || item.name,
        address: details?.address || item.address || '',
        latitude: details?.latitude ?? null,
        longitude: details?.longitude ?? null,
        placeId: details?.placeId || item.placeId
      };

      setAddress(structured.address);
      setHasSelectedPlace(true);

      if (onChange) {
        onChange(structured);
      }
    } catch (err) {
      console.error('Error fetching place details on select:', err);
      // Fallback to suggestion info
      const fallback = {
        name: item.name,
        address: item.address || '',
        latitude: null,
        longitude: null,
        placeId: item.placeId
      };
      setAddress(fallback.address);
      setHasSelectedPlace(true);
      if (onChange) {
        onChange(fallback);
      }
    } finally {
      setIsResolvingDetails(false);
    }
  };

  // Clear selection
  const handleClear = () => {
    setQuery('');
    setAddress('');
    setSuggestions([]);
    setIsOpen(false);
    setHasSelectedPlace(false);
    sessionTokenRef.current = null;
    if (onChange) {
      onChange({
        name: '',
        address: '',
        latitude: null,
        longitude: null,
        placeId: null
      });
    }
  };

  return (
    <div ref={wrapperRef} className="relative w-full">
      <div className="relative flex items-center">
        <input
          id={id}
          type="text"
          required={required}
          value={query}
          onChange={handleInputChange}
          onFocus={() => {
            if (query.trim().length >= 3 && suggestions.length > 0) {
              setIsOpen(true);
            }
          }}
          placeholder={placeholder}
          className={`w-full px-3.5 pr-8 h-[38px] bg-white border border-slate-200 focus:border-[#067978] rounded-xl text-xs font-normal text-slate-800 outline-none transition-all shadow-2xs ${className}`}
        />

        <div className="absolute right-2.5 flex items-center gap-1">
          {isSearching || isResolvingDetails ? (
            <Loader2 size={14} className="animate-spin text-[#067978]" />
          ) : query ? (
            <button
              type="button"
              onClick={handleClear}
              className="text-slate-400 hover:text-slate-600 p-0.5 rounded cursor-pointer"
              title="Clear"
            >
              <X size={13} />
            </button>
          ) : null}
        </div>
      </div>

      {/* Structured Address / Verified Pill */}
      {address ? (
        <div className="flex items-center gap-1.5 text-[11px] text-slate-500 mt-1 pl-1">
          <MapPin size={11} className="text-[#067978] shrink-0" />
          <span className="truncate max-w-[340px]" title={address}>
            {address}
          </span>
          {hasSelectedPlace && (
            <span className="inline-flex items-center gap-0.5 text-[10px] font-medium text-emerald-700 bg-emerald-50 px-1.5 py-0.2 rounded-full border border-emerald-200/60 ml-auto shrink-0">
              <Check size={9} /> Verified
            </span>
          )}
        </div>
      ) : null}

      {/* Autocomplete Dropdown */}
      {isOpen && (
        <div className="absolute left-0 right-0 top-full mt-1.5 z-50 bg-white border border-slate-200 rounded-xl shadow-xl overflow-hidden max-h-56 overflow-y-auto divide-y divide-slate-100 animate-in fade-in-50 duration-150">
          {isSearching ? (
            <div className="px-4 py-3 flex items-center gap-2 text-xs text-slate-500">
              <Loader2 size={13} className="animate-spin text-[#067978]" />
              <span>Searching Google Places...</span>
            </div>
          ) : suggestions.length > 0 ? (
            suggestions.map((item) => (
              <button
                key={item.placeId}
                type="button"
                onClick={() => handleSelectSuggestion(item)}
                className="w-full text-left px-3.5 py-2.5 hover:bg-slate-50/90 active:bg-slate-100 flex items-start gap-2.5 transition-colors cursor-pointer group"
              >
                <div className="p-1.5 rounded-lg bg-teal-50 text-[#067978] group-hover:bg-[#067978] group-hover:text-white transition-colors mt-0.5 shrink-0">
                  <MapPin size={13} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-semibold text-slate-800 truncate">
                    {item.name}
                  </div>
                  {item.address ? (
                    <div className="text-[11px] text-slate-500 truncate mt-0.5">
                      {item.address}
                    </div>
                  ) : null}
                </div>
              </button>
            ))
          ) : query.trim().length >= 3 ? (
            <div className="px-4 py-3 text-center text-xs text-slate-500">
              <p className="font-medium text-slate-600">No matching places found</p>
              <p className="text-[11px] text-slate-400 mt-0.5">
                You can keep typing to save "{query}" as a custom location.
              </p>
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
};

export default LocationAutocompleteInput;
